import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import {
  formatNumber,
  tooltipContentStyle,
  tooltipCursorStyle,
} from "utils/dashboardUtils";
import { formatLabel, isDesktop } from "utils/utils";

const RevenueStreamChart = ({ activeFilterType }) => {
  const { t } = useTranslation();

  const [isMobile, setIsMobile] = useState(!isDesktop);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const revenueData = useSelector(
    (state) => state.Dashboard?.data?.charts?.revenueChart,
  );

  const COLORS = {
    membership: "#53c3eb",
    oneTime: "#de235c",
    addons: "#523677",
  };

const getSubtitle = () => {
  switch (activeFilterType) {
    case "DAY":
      return t("Revenue trends over the last 24 hours");

    case "WEEK":
      return t("Revenue trends for the current week");

    case "MONTH":
      return t("Revenue trends over the last 30 days");

    case "YEAR":
      return t("Revenue trends over the last 12 months");

    default:
      return t("Revenue trends");
  }
};

  const isAllZero = revenueData?.every((item) => {
    return (
      (item.membership ?? 0) === 0 &&
      (item.oneTime ?? 0) === 0 &&
      (item.addons ?? 0) === 0
    );
  });
  const CustomizedAxisTick = ({ x, y, payload, activeFilterType }) => {
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="end"
          style={{ direction: "ltr" }}
          fill="#6B7280"
          fontSize={11}
          fontWeight={500}
          transform="rotate(-35)"
        >
          {formatLabel(payload.value, activeFilterType)}
        </text>
      </g>
    );
  };
  const shouldRotate = isMobile || revenueData?.length > 6;

  const xAxisInterval = isMobile ? 1 : 0;

  return (
    <div className="revenue_chart dashboard_common_card">
      {/* Header */}
      <div className="revenue_chart__header mb-4">
        <div>
          <h4 className="fs_20">{t("Revenue Stream Analysis")}</h4>
          <p className="page_sub_title">{getSubtitle()}</p>
        </div>

        <div className="revenue_chart__legend">
          <span className="legend membership">{t("Membership")}</span>
          <span className="legend oneTime">{t("One Time Wash")}</span>
          <span className="legend addons">{t("Add-ons")}</span>
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={340}>
        <BarChart
          data={revenueData}
          barCategoryGap={isMobile ? "10%" : "25%"}
          margin={{
            top: 20,
            right: isMobile ? 5 : 10,
            left: isMobile ? 5 : isAllZero ? 0 : 10,
            bottom: isMobile ? 0 : 10,
          }}
        >
          <CartesianGrid strokeWidth={0.4} vertical={false} stroke="#E5E7EB" />

          <Tooltip
            formatter={(value, name) => {
              const labels = {
                membership: t("Membership"),
                oneTime: t("One Time Wash"),
                addons: t("Add-ons"),
              };
              return [
                `SAR ${Number(value ?? 0).toLocaleString()}`,
                labels[name],
              ];
            }}
            labelFormatter={(label) => formatLabel(label, activeFilterType)}
            cursor={tooltipCursorStyle}
            contentStyle={tooltipContentStyle}
          />

          <XAxis
            dataKey="label"
            padding={isMobile ? { left: 0, right: 0 } : undefined}
            axisLine={false}
            tickLine={false}
            interval={xAxisInterval}
            height={shouldRotate ? 60 : 30}
            tick={
              shouldRotate
                ? (props) => (
                    <CustomizedAxisTick
                      {...props}
                      activeFilterType={activeFilterType}
                    />
                  )
                : { fill: "#6B7280", fontSize: 14, fontWeight: 500 }
            }
            tickMargin={shouldRotate ? 0 : 12}
            tickFormatter={
              shouldRotate
                ? undefined
                : (label) => formatLabel(label, activeFilterType)
            }
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            width={isAllZero ? 0 : isMobile ? 40 : 80}
            ticks={isAllZero ? [0, 25, 50, 75, 100] : undefined}
            domain={
              isAllZero
                ? [0, 100]
                : [
                    0,
                    (dataMax) => {
                      const padded = dataMax * 1.15;
                      const magnitude = Math.pow(
                        10,
                        Math.floor(Math.log10(padded)),
                      );
                      return Math.ceil(padded / magnitude) * magnitude;
                    },
                  ]
            }
            tick={{
              fill: "#374151",
              fontSize: isMobile ? 11 : 13,
              style: { direction: "ltr" },
            }}
            tickFormatter={(value) => {
              const prefix = isMobile ? "" : "SAR ";
              if (value === 0) return `${prefix}0`;
              if (value >= 1_000_000)
                return `${prefix}${(value / 1_000_000).toFixed(1)}M`;
              if (value >= 1_000)
                return `${prefix}${(value / 1_000).toFixed(0)}K`;
              return `${prefix}${value}`;
            }}
          />

          <Bar
            dataKey="membership"
            stackId="revenue"
            fill={COLORS.membership}
            radius={[4, 4, 0, 0]}
          />
          <Bar dataKey="oneTime" stackId="revenue" fill={COLORS.oneTime} />
          <Bar
            dataKey="addons"
            stackId="revenue"
            fill={COLORS.addons}
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default RevenueStreamChart;
